<template>
  <div class="video-preview">
    <!-- 顶部导航栏 -->
    <van-nav-bar
      title="视频直播"
      left-text="返回"
      left-arrow
      @click-left="goBack"
      class="nav-bar"
    />
    
    <!-- 页面标题区域 -->
    <div class="page-header">
      <div class="page-title">
        <van-icon name="video-o" class="title-icon" />
        <span>设备视频监控</span>
      </div>
      <div class="page-subtitle">实时查看设备摄像头画面和历史记录</div>
    </div>
    
    <!-- 设备信息 -->
    <div class="device-info-card" v-if="deviceId">
      <div class="info-row">
        <span class="label">设备编号：</span>
        <span class="value">{{ deviceId }}</span>
      </div>
    </div>
    
    <!-- 摄像头选择 -->
    <div class="camera-selector">
      <van-field
        readonly
        clickable
        name="camera"
        label="选择摄像头"
        :value="selectedCameraName"
        placeholder="请选择摄像头"
        @click="showCameraPicker = true"
      />
    </div>

    <!-- 摄像头选择器弹窗 -->
    <van-popup v-model="showCameraPicker" position="bottom">
      <van-picker
        :columns="cameraOptions"
        show-toolbar
        title="选择摄像头"
        @confirm="onCameraPickerConfirm"
        @cancel="showCameraPicker = false"
      />
    </van-popup>

    <!-- 视频显示区域 -->
    <div class="video-container">
      <div v-if="!selectedCamera" class="no-camera-selected">
        <van-icon name="video-o" size="64" color="#ddd" />
        <p>请选择摄像头查看画面</p>
      </div>
      
      <div v-else class="video-display">
        <div class="video-frame">
          <!-- flv.js播放器容器 -->
          <video ref="flvPlayer" controls autoplay style="width:100%;height:100%;background:#000;"></video>
          <!-- 静态画面 -->
          <img
            v-if="!isLiveStreaming && currentFrame"
            :src="currentFrame"
            alt="摄像头画面"
            class="video-image"
            @error="onImageError"
          />
          <!-- 加载状态 -->
          <div v-if="!isLiveStreaming && !currentFrame" class="loading-frame">
            <van-loading size="24px" />
            <p>加载画面中...</p>
          </div>
          <!-- 播放控制覆盖层 -->
          <div v-if="!isLiveStreaming && selectedCamera" class="play-overlay">
            <van-button
              type="primary"
              size="large"
              icon="play-circle-o"
              @click="startLiveStreaming"
              :loading="startingStream"
              class="play-button"
            >
              {{ startingStream ? '正在启动直播...' : '开始直播' }}
            </van-button>
          </div>
        </div>
        
      
      </div>
    </div>

    <!-- 控制面板 -->
    <div v-if="selectedCamera" class="control-panel">
      <div class="control-buttons">
        <!-- 直播控制按钮 -->
        <van-button
          type="danger"
          size="small"
          icon="stop-circle-o"
          @click="stopLiveStreaming"
          :loading="stoppingStream"
          :disabled="!isLiveStreaming"
        >
          停止直播
        </van-button>
        
        <!-- 静态画面控制 -->
        <!-- <van-button
          v-if="!isLiveStreaming"
          type="default"
          size="small"
          :loading="refreshing"
          @click="refreshFrame"
        >
          刷新画面
        </van-button> -->
        
        <!-- <van-button
          v-if="!isLiveStreaming"
          type="default"
          size="small"
          @click="toggleAutoRefresh"
        >
          {{ autoRefresh ? '停止自动刷新' : '开启自动刷新' }}
        </van-button> -->
        
        <!-- <van-button
          type="success"
          size="small"
          @click="captureFrame"
        >
          截图保存
        </van-button> -->
      </div>
      
      <!-- 自动刷新间隔设置 -->
      <!-- <div v-if="!isLiveStreaming" class="refresh-interval">
        <span class="interval-label">刷新间隔：</span>
        <van-slider
          v-model="refreshInterval"
          :min="1"
          :max="10"
          :step="1"
          @change="onIntervalChange"
          class="interval-slider"
        />
        <span class="interval-value">{{ refreshInterval }}秒</span>
      </div> -->
    </div>



    <!-- 历史记录预览 -->
    <van-image-preview
      v-model="showHistoryPreview"
      :images="previewImages"
      :start-position="previewIndex"
      :closeable="true"
      close-icon="cross"
      close-icon-position="top-right"
    />
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'VideoPreview',
  props: {
    deviceId: {
      type: String,
      default: ''
    },
    mac: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedCamera: null,
      currentFrame: null,
      lastUpdateTime: '',
      refreshing: false,
      autoRefresh: false,
      refreshInterval: 3,
      refreshTimer: null,
      showCameraPicker: false,
      loadingHistory: false,
      showHistoryPreview: false,
      previewImages: [],
      previewIndex: 0,
      
      // 直播相关
      isLiveStreaming: false,
      startingStream: false,
      stoppingStream: false,
      playAddress: '',
      streamDuration: 0,
      remainingTime: 0,
      streamStartTime: 0,
      continueTimer: null,
      requestId: '',
      networkStatus: 'online', // 网络状态
      flvInstance: null,
      cameras: [],
      historyRecords: [],
      cameraPickerValue: null
    };
  },
  
  computed: {
    selectedCameraName() {
      if (!this.selectedCamera) return '';
      return `${this.selectedCamera.deviceSN} - ${this.getCameraTypeText(this.selectedCamera.type)}`;
    },
    
    cameraOptions() {
      return this.cameras.map(camera => ({
        text: `${camera.deviceSN} - ${this.getCameraTypeText(camera.type)} - ${camera.isOnline ? '在线' : '离线'}`,
        value: camera.id
      }));
    }
  },
  
  methods: {
    goBack() {
      this.$router.push('/home/device-management');
    },
    
    onCameraConfirm(value) {
      const camera = this.cameras.find(c => c.id === value.value);
      if (camera) {
        this.selectedCamera = camera;
        this.showCameraPicker = false;
        // 如果正在直播，停止直播
        if (this.isLiveStreaming) {
          this.stopLiveStreaming();
        }
        this.loadCameraFrame();
      }
    },
    
    onCameraPickerConfirm(value, index) {
      const camera = this.cameras.find(c => c.id === value.value);
      if (camera) {
        // 检查摄像头是否离线
        if (!camera.isOnline) {
          this.$toast.fail('该摄像头离线，不能直播');
          return;
        }
        
        this.selectedCamera = camera;
        this.showCameraPicker = false;
        if (this.isLiveStreaming) {
          this.stopLiveStreaming();
        }
        this.loadCameraFrame();
      } else {
        this.$toast('请选择摄像头');
      }
    },
    
    async loadCameraFrame() {
      if (!this.selectedCamera || !this.selectedCamera.isOnline) {
        this.currentFrame = null;
        return;
      }
      
      try {
        // 模拟获取视频帧（base64字符串）
        // 实际应用中这里应该调用真实的API
        const response = await this.mockGetCameraFrame();
        this.currentFrame = response.frame;
        this.lastUpdateTime = new Date().toLocaleTimeString('zh-CN');
      } catch (error) {
        console.error('获取摄像头画面失败:', error);
        this.$toast.fail('获取画面失败');
      }
    },
    
    mockGetCameraFrame() {
      // 模拟API调用
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            frame: 'https://img.yzcdn.cn/vant/cat.jpeg', // 实际应该是base64字符串
            timestamp: Date.now()
          });
        }, 500);
      });
    },
    
    async playFlvByAddress(flvUrl) {
      this.destroyFlv();
      await this.loadFlvScript();
      const video = this.$refs.flvPlayer;
      if (window.flvjs && window.flvjs.isSupported()) {
        video.removeAttribute('src');
        video.load();
        
        // 通过代理服务器转发请求
        let finalUrl = flvUrl;
        if (flvUrl.startsWith('http://')) {
          // 方案1：使用代理接口转发HTTP请求
          const proxyUrl = `/api/proxy/video?url=${encodeURIComponent(flvUrl)}`;
          finalUrl = proxyUrl;
          
          // 方案2：如果代理不可用，尝试使用iframe（需要后端支持）
          // 这里可以添加iframe相关逻辑
        }
        
        this.flvInstance = window.flvjs.createPlayer({
          type: 'flv',
          url: finalUrl
        });
        this.flvInstance.attachMediaElement(video);
        this.flvInstance.load();
        this.flvInstance.play();
        this.flvInstance.on(window.flvjs.Events.ERROR, (errType, errDetail, errInfo) => {
          console.error('FLV播放错误:', errType, errDetail, errInfo);
          this.$toast.fail('FLV播放失败: ' + errDetail);
        });
      } else {
        this.$toast.fail('当前浏览器不支持FLV播放');
      }
    },
    destroyFlv() {
      if (this.flvInstance) {
        this.flvInstance.destroy();
        this.flvInstance = null;
      }
      const video = this.$refs.flvPlayer;
      if (video) {
        video.removeAttribute('src');
        video.load();
      }
    },
    loadFlvScript() {
      return new Promise((resolve, reject) => {
        if (window.flvjs) return resolve();
        if (document.getElementById('flvjs-script')) {
          document.getElementById('flvjs-script').onload = resolve;
          return;
        }
        const script = document.createElement('script');
        script.id = 'flvjs-script';
        script.src = 'https://cdn.jsdelivr.net/npm/flv.js@latest/dist/flv.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    },
    // 开始直播
    async startLiveStreaming() {
      if (!this.selectedCamera) {
        this.$toast.fail('请先选择摄像头');
        return;
      }
      
      if (!this.selectedCamera.isOnline) {
        this.$toast.fail('该摄像头离线，不能直播');
        return;
      }
      
      if (!this.mac) {
        this.$toast.fail('设备MAC为空，无法请求直播');
        return;
      }
      
      this.startingStream = true;
      try {
        const response = await this.requestPlayStreaming('play');
        if (response.success) {
          this.isLiveStreaming = true;
          this.playAddress = response.data.play_address;
          this.streamDuration = response.data.duration;
          this.requestId = response.data.requestId;
          this.streamStartTime = Date.now();
          this.remainingTime = this.streamDuration;
          // 启动时长监控
          this.startDurationMonitoring();
          this.$toast.success('直播已开始，正在等待设备推流...');
          setTimeout(() => {
            this.playFlvByAddress(this.playAddress);
          }, 5000);
        } else {
          this.$toast.fail(response.message || '启动直播失败');
        }
      } catch (error) {
        // 如果接口有返回message字段，优先显示
        if (error && error.response && error.response.data && error.response.data.message) {
          this.$toast.fail(error.response.data.message);
        } else if (error && error.message) {
          this.$toast.fail(error.message);
        } else {
          this.$toast.fail('启动直播失败，请检查网络连接');
        }
      } finally {
        this.startingStream = false;
      }
    },
    
    // 停止直播
    async stopLiveStreaming() {
      this.stoppingStream = true;
      try {
        await this.requestPlayStreaming('stop');
        this.isLiveStreaming = false;
        this.playAddress = '';
        this.streamDuration = 0;
        this.remainingTime = 0;
        this.requestId = '';
        
        // 停止时长监控
        this.stopDurationMonitoring();
        this.destroyFlv();
        
        this.$toast.success('直播已停止');
      } catch (error) {
        console.error('停止直播失败:', error);
        this.$toast.fail('停止直播失败');
      } finally {
        this.stoppingStream = false;
      }
    },
    
    // 请求播放流
    async requestPlayStreaming(command) {
      this.$axios = this.$axios || require('@/utils/request').default;
      
      const params = {
        requestId: this.requestId || uuidv4(),
        devId: this.mac,
        camIndex: this.selectedCamera.camIndex,
        command: command,
        timestamp: Date.now(),        
        camIp: this.selectedCamera.ip || ''
      };
      
      const response = await this.$axios.post('/api/LiveStreaming/PostPlayStreaming', params);
      return response.data;
    },  
 
    
    // 启动时长监控
    startDurationMonitoring() {
      this.stopDurationMonitoring();
      this.continueTimer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - this.streamStartTime) / 1000);
        this.remainingTime = Math.max(0, this.streamDuration - elapsed);
        
        // 如果剩余时间少于20秒，自动续播
        if (this.remainingTime <= 20 && this.remainingTime > 0) {
          this.continueStreaming();
        }
        
        // 如果时间到了，停止直播
        if (this.remainingTime <= 0) {
          this.stopLiveStreaming();
        }
      }, 1000);
    },
    
    // 停止时长监控
    stopDurationMonitoring() {
      if (this.continueTimer) {
        clearInterval(this.continueTimer);
        this.continueTimer = null;
      }
    },
    
    // 续播
    async continueStreaming() {
      try {
        const response = await this.requestPlayStreaming('continue');
        if (response.success) {
          // 更新时长
          this.streamDuration = response.data.duration;
          this.remainingTime = this.streamDuration;
          this.streamStartTime = Date.now();
          console.log('直播已续播，新时长:', this.streamDuration);
          this.$toast.success('直播已自动续播');
        } else {
          console.warn('续播失败:', response.message);
          this.$toast.fail('续播失败，直播可能即将结束');
        }
      } catch (error) {
        console.error('续播失败:', error);
        this.$toast.fail('续播失败，请检查网络连接');
      }
    },
    
    // 格式化时长显示
    formatDuration(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    
    // 视频播放器事件处理
    onVideoError(event) {
      console.error('视频播放错误:', event);
      this.$toast.fail('视频播放失败');
    },
    
    onVideoLoadStart() {
      console.log('视频开始加载');
    },
    
    onVideoCanPlay() {
      console.log('视频可以播放');
    },
    
    onVideoTimeUpdate() {
      // 视频播放进度更新
    },
    
    async refreshFrame() {
      this.refreshing = true;
      await this.loadCameraFrame();
      this.refreshing = false;
    },
    
    toggleAutoRefresh() {
      this.autoRefresh = !this.autoRefresh;
      
      if (this.autoRefresh) {
        this.startAutoRefresh();
        this.$toast.success(`已开启自动刷新，间隔${this.refreshInterval}秒`);
      } else {
        this.stopAutoRefresh();
        this.$toast.success('已停止自动刷新');
      }
    },
    
    startAutoRefresh() {
      this.stopAutoRefresh(); // 先停止之前的定时器
      this.refreshTimer = setInterval(() => {
        this.loadCameraFrame();
      }, this.refreshInterval * 1000);
    },
    
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    
    onIntervalChange(value) {
      this.refreshInterval = value;
      if (this.autoRefresh) {
        this.startAutoRefresh(); // 重新启动定时器
      }
    },
    
    captureFrame() {
      if (!this.currentFrame && !this.isLiveStreaming) {
        this.$toast.fail('当前无画面可截图');
        return;
      }
      
      // 模拟截图保存
      this.$toast.success('截图已保存到相册');
    },
    
    loadHistory() {
      this.loadingHistory = true;
      setTimeout(() => {
        // 模拟加载更多历史记录
        const newRecords = [
          {
            id: this.historyRecords.length + 1,
            time: '14:10:15',
            thumbnail: 'https://img.yzcdn.cn/vant/cat.jpeg',
            fullImage: 'https://img.yzcdn.cn/vant/cat.jpeg'
          }
        ];
        this.historyRecords.push(...newRecords);
        this.loadingHistory = false;
        this.$toast.success('加载完成');
      }, 1000);
    },
    
    previewHistory(record) {
      this.previewImages = [record.fullImage];
      this.previewIndex = 0;
      this.showHistoryPreview = true;
    },
    
    onImageError() {
      this.$toast.fail('画面加载失败');
      this.currentFrame = null;
    },
    
    // 获取摄像头类型文本
    getCameraTypeText(type) {
      switch (Number(type)) {
        case 0: return '人脸摄像头';
        case 1: return 'AI摄像头';
        default: return '未知类型';
      }
    },
    
    // 检测网络状态
    checkNetworkStatus() {
      if (navigator.onLine) {
        this.networkStatus = 'online';
      } else {
        this.networkStatus = 'offline';
        if (this.isLiveStreaming) {
          this.$toast.fail('网络连接已断开，直播可能受到影响');
        }
      }
    },
    
    // 加载摄像头列表
    async loadCameras() {
      if (!this.deviceId) {
        console.warn('设备ID为空，无法加载摄像头列表');
        return;
      }
      
      try {
        this.$axios = this.$axios || require('@/utils/request').default;
        const response = await this.$axios.post('/api/TKCamera/Query', {
          bindingTKId: this.deviceId
        });
        
        if (response && response.data && response.data.success) {
          this.cameras = response.data.data || [];
          console.log('摄像头列表加载成功:', this.cameras);
        } else {
          this.$toast.fail('获取摄像头列表失败');
        }
      } catch (error) {
        console.error('加载摄像头列表失败:', error);
        this.$toast.fail('获取摄像头列表失败');
      }
    },
    
    // 监听网络状态变化
    setupNetworkListener() {
      window.addEventListener('online', () => {
        this.networkStatus = 'online';
        this.$toast.success('网络连接已恢复');
      });
      
      window.addEventListener('offline', () => {
        this.networkStatus = 'offline';
        this.$toast.fail('网络连接已断开');
      });
    }
  },
  
  mounted() {
    // 设置网络状态监听
    //this.setupNetworkListener();
    //this.checkNetworkStatus();
    
    // 如果有设备ID，加载对应设备的摄像头信息
    if (this.deviceId) {
      this.loadCameras();
    }
  },
  
  beforeDestroy() {
    this.stopAutoRefresh();
    this.stopDurationMonitoring();
    this.destroyFlv && this.destroyFlv();
    // 如果正在直播，停止直播
    if (this.isLiveStreaming) {
      this.stopLiveStreaming();
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.isLiveStreaming) {
      console.log('beforeRouteLeave','通知停止直播');
      this.stopLiveStreaming().then(() => next());
    } else {
      next();
    }
  }
};
</script>

<style scoped>
.video-preview {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.nav-bar {
  background: #e9ecef !important;
  color: #495057 !important;
  border-bottom: 3px solid #dee2e6 !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
  height: 44px !important;
}

.nav-bar :deep(.van-nav-bar__title) {
  color: #343a40 !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
}

.nav-bar :deep(.van-nav-bar__left) {
  color: #6c757d !important;
}

.nav-bar :deep(.van-nav-bar__text) {
  color: #6c757d !important;
  font-size: 14px !important;
}

.nav-bar :deep(.van-icon) {
  color: #6c757d !important;
  font-size: 16px !important;
}

.page-header {
  padding: 16px 12px 8px 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
}

.title-icon {
  color: #6c757d;
  font-size: 20px;
}

.page-subtitle {
  font-size: 13px;
  color: #6c757d;
  margin-left: 28px;
}

.device-info-card {
  margin: 12px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

.camera-selector {
  background: white;
  padding: 16px;
  border-bottom: 1px solid #ebedf0;
  margin: 0 12px;
  border-radius: 8px 8px 0 0;
}

.video-container {
  margin: 0 12px 12px 12px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.no-camera-selected {
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #969799;
}

.no-camera-selected p {
  margin: 16px 0 0 0;
  font-size: 14px;
}

.video-display {
  padding: 16px;
}

.video-frame {
  width: 100%;
  height: 240px;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  margin-bottom: 16px;
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.loading-frame {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
}

.loading-frame p {
  margin: 8px 0 0 0;
  font-size: 14px;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
}

.play-button {
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid white;
  color: white;
  font-weight: bold;
}

.play-button:hover {
  background: rgba(0, 0, 0, 0.8);
}

.video-info {
  background: #f7f8fa;
  border-radius: 8px;
  padding: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.label {
  color: #969799;
  font-size: 14px;
}

.value {
  color: #323233;
  font-size: 14px;
  font-weight: 500;
}

.control-panel {
  margin: 12px;
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

.control-buttons {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.control-buttons .van-button {
  flex: 1;
  min-width: 0;
}

.refresh-interval {
  display: flex;
  align-items: center;
  gap: 10px;
}

.interval-label {
  font-size: 14px;
  color: #323233;
  white-space: nowrap;
}

.interval-slider {
  flex: 1;
}

.interval-value {
  font-size: 14px;
  color: #323233;
  white-space: nowrap;
  min-width: 30px;
}

.history-section {
  margin: 12px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #ebedf0;
}

.section-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  color: #323233;
}

.history-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
  padding: 16px;
}

.history-item {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.history-item:active {
  transform: scale(0.95);
}

.history-thumbnail {
  width: 100%;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #ebedf0;
}

.history-time {
  font-size: 10px;
  color: #969799;
  text-align: center;
  margin-top: 4px;
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  .video-preview {
    background-color: #1a1a1a;
  }
  
  .page-header {
    background: linear-gradient(135deg, #2a2a2a 0%, #333 100%);
    border-bottom-color: #444;
  }
  
  .page-title {
    color: #ccc;
  }
  
  .title-icon {
    color: #999;
  }
  
  .page-subtitle {
    color: #999;
  }
  
  .device-info-card,
  .camera-selector,
  .video-container,
  .control-panel,
  .history-section {
    background: #2a2a2a;
    border-color: #444;
  }
  
  .video-info {
    background: #333;
  }
  
  .value {
    color: #ccc;
  }
  
  .interval-label {
    color: white;
  }
  
  .interval-value {
    color: white;
  }
  
  .section-header {
    border-bottom-color: #444;
  }
  
  .section-header h3 {
    color: white;
  }
  
  .history-thumbnail {
    border-color: #555;
  }
  
  .nav-bar {
    background: #333 !important;
    color: #ccc !important;
    border-bottom-color: #555 !important;
  }
  
  .nav-bar :deep(.van-nav-bar__title) {
    color: #e9ecef !important;
  }
  
  .nav-bar :deep(.van-nav-bar__left) {
    color: #999 !important;
  }
  
  .nav-bar :deep(.van-nav-bar__text) {
    color: #999 !important;
  }
  
  .nav-bar :deep(.van-icon) {
    color: #999 !important;
  }
}
</style>
